<script setup>
import { ref, onMounted, computed, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import { usePageLoadingStore } from "../../../../stores/pageLoading";
import adminCorporationService from "../../services/corporation/adminCorporationService";
import jobFairUserService from "../../../user/services/jobFair/jobFairUserService";
import jobFairCorpService from "../../../user/services/jobFair/jobFairCorpService";
import adminJobfairService from "../../services/jobfair/adminJobfairService";
import adminApprovedJoinStateService from "../../services/jobfair/adminApprovedJoinStateService";
import fileService from "@/services/fileService";
import SNBAdmin from "../../../../layouts/nav/SNBAdmin.vue";
import KSATextButton from "../../../../components/ui-components/button/KSATextButton.vue";
import KSAPrimaryButton from "../../../../components/ui-components/button/KSAPrimaryButton.vue";
import KSASecondaryButton from "../../../../components/ui-components/button/KSASecondaryButton.vue";
import KSAInput from "@/components/ui-components/input/KSAInput.vue";
import KSAInputNumber from "@/components/ui-components/input/KSAInputNumber.vue";
import KSAInputGeneralPhoneNum from "@/components/ui-components/input/KSAInputGeneralPhoneNum.vue";
import JobFairJobNoticeModal from "@/pages/user/components/modal/jobFair/JobFairJobNoticeModal.vue";
import JobFairResumeModal from "@/pages/user/components/modal/jobFair/JobFairResumeModal.vue";
import _ from "lodash";

const route = useRoute();
const router = useRouter();
const loadingStore = usePageLoadingStore();
const { $pageListBoardNo, $toast, $number, $gbNameKR, $yyyymmddHHmmss, $interests, $intvGb, $applyStateGb, $userIntvStateGb, $regex, $gb : { EMPLOYEE_NUM_GB, SALES_AMT_GB } } = getCurrentInstance().appContext.config.globalProperties;
const loaded = ref(false); // 데이터 받고 난 후, 화면 그리는 용도

const ojfId = ref(route.query.ojfId || '');
const corpId = ref(route.query.corpId || '');

const BASIC        = 'BASIC';
const ENTRY_UPDATE = 'ENTRY_UPDATE';             // 참가신청 정보 관리 (TAB 1) 수정 화면
const RETRIEVE_APPLICANT = 'RETRIEVE_APPLICANT'; // 채용공고 관리 및 지원 현황 (TAB 2) > 입사지원

const intvDays = ref(null);      // 면접 가능일
const isAllActive = ref(false);  // 면접일 전체
const isEtcActive = ref(false);  // 면접일 외 일정
const inputError = ref({
    joinPurpose: '',
    intvAbleDay: '',
    intvGb: '',
    fileYn: '',
    corpResumeAttaches: '',
    recruitCnt: '',
})
const corpResumeAttaches = () => {
    recruitmentHallInfo.value.corpResumeAttaches.push({
        attachSeq: 0,
        fileName: '',
        fileSize: '',
        fileDir: '',
        fileOrder: 0,
        deleteYn: ''
    })
}

const ojfDetail = ref({});                  // 행사 정보(for 행사명)
const recruitmentHallInfo = ref(null);      // 참가신청 정보 관리 TAB(1)
const jobNoticeList = ref([]);              // 채용공고 관리 및 지원 현황 TAB(2)
const applicantList = ref([]);              // 채용공고 관리 및 지원 현황 TAB(2) > 입사지원 리스트
const interestEntryList = ref([]);          // 관심인재 TAB(3)
const interviewProposalEntryList = ref([]); // 면접제안 인재 TAB(4)

onMounted( async () => {
    await ojfIntvDay();        // 행사 면접 가능일 조회
    retrieveOnlineJobFair();   // 행사 정보 조회(for 행사명)
    retrieveRecruitmentHall(); // 참가신청 정보 조회
})

// tabIndex
const tabOptions = ref([
    {label: '참가신청 정보 관리', value: 1},
    {label: '채용공고 관리 및 지원 현황', value: 2},
    {label: '관심인재', value: 3},
    {label: '면접제안 인재', value: 4},
])

const currentPage = ref(1);
const pageSize = ref(10);
const totalPages = ref(0);
const totalCount = ref(0);
const searchOpen = ref(true);

const tabIndex = ref(1);

const modal = ref(null);
const confirmModal = ref(null);
const jobNoticeModal = ref(null); // 채용공고
const jobFairResumeModal = ref(null); // 이력서
const modalContent = ref([]);
const okButtonText = ref("다운로드");

// 검색조건 - 페이지 크기
const pageSizeGroup = ref([
    { label:'10개씩 보기', value: '10' },
    { label:'30개씩 보기', value: '30' },
    { label:'50개씩 보기', value: '50' },
    { label:'100개씩 보기', value: '100' },
])

// 면접 가능일 전체인지 확인 후 문구 표시
// 참가신청 정보 관리
const allDays = computed(() => recruitmentHallInfo.value.intvAbleDay.split(','))
const isAllSelected = computed(() => intvDays.value.length === allDays.value.length)

// 채용공고 관리 및 지원 현황 > 채용공고
const allDays2 = computed(() => applicantList.value.flatMap(item => item.intvAbleDay.split(',')))
const isAllSelected2 = computed(() => intvDays.value.length === allDays2.value.length)

// 관심인재
const allDays3 = computed(() => interestEntryList.value.flatMap(item => item.intvAbleDay.split(',')))
const isAllSelected3 = computed(() => intvDays.value.length === allDays3.value.length)

// 면접제안 인재
const allDays4 = computed(() => interviewProposalEntryList.value.flatMap(item => item.intvAbleDay.split(',')))
const isAllSelected4 = computed(() => intvDays.value.length === allDays4.value.length)

// viewMode 관리
// 참가신청 정보 관리 (TAB 1)
const viewMode = ref(BASIC); // JOB_NOTICE
// 탭 상세 화면 보기 조건
const setViewMode = (viewName, jobNoticeSeq) => {
    // 공통 사용변수 초기화
    currentPage.value = 1;
    pageSize.value = 10;

    applicantJobNoticeSeq.value = jobNoticeSeq;

    if ( viewName === BASIC ) {
        retrieveList();
    } else if ( viewName === ENTRY_UPDATE ) { // 참가신청 정보 관리 수정 화면

        // 선택한 면접 날짜 가져오기
        const selectedDays = recruitmentHallInfo.value.intvAbleDay?.split(',').map(day => day.trim()) || []

        setTimeout(() => {
            // 면접 가능 전체 날짜
            const allLabels = intvDays.value.map(day => day.label)

            if (recruitmentHallInfo.value.intvInYn === 'Y') {
                // 면접 가능일내 날짜

                // "전체" 선택 여부 판단
                const isAll = selectedDays.length === allLabels.length &&
                    selectedDays.every(day => allLabels.includes(day))

                if (isAll) {
                    onClickAll(true)
                } else {
                    isAllActive.value = false
                    isEtcActive.value = false
                    recruitmentHallInfo.value.intvInYn = 'Y'

                    intvDays.value.forEach(day => {
                        day.on = selectedDays.includes(day.label)
                    })
                }

                if(_.isEmpty(recruitmentHallInfo.value.corpResumeAttaches)) {
                    corpResumeAttaches();
                }
            } else {
                // 그 외
                onClickEtc(true)
            }
        }, 100)
    } else if (viewName === RETRIEVE_APPLICANT) {
        retrieveOjfResumeListByByJobNoticeSeqAdminPaging(jobNoticeSeq);
    } else {
        ojfIntvDay();        // 행사 면접 가능일 조회
        retrieveOnlineJobFair();   // 행사 정보 조회(for 행사명)
        retrieveRecruitmentHall(); // 참가신청 정보 조회
    }

    viewMode.value = viewName;
}

// 채용공고 관리 및 지원 현황 (TAB 2)
const applicantJobNoticeSeq = ref(); // 채용공고 관리 및 지원 현황 > 선택한 채용공고seq

// START: 참가신청 정보 관리 TAB

// 1. 면접 가능일 조회. for 검색
const ojfIntvDay = async () => {
    loadingStore.loading = true;

    try {
        const res = await jobFairUserService.ojfIntvDay(ojfId.value);
        loadingStore.loading = false;

        if (res.data.code.toLowerCase() === 'success') {
            const result = res.data.result;
            if (result) {
                const selectedDays = result.split(',').map(d => d.trim());

                intvDays.value = selectedDays.map(day => ({
                  label: day,
                  on: false
                }));
            }
        }
    } catch (error) {
        console.error(error);
        loadingStore.loading = false;
    }
}

// 2. 행사 정보 조회 for 상단 행사명 타이틀
const retrieveOnlineJobFair = async () => {
    loadingStore.loading = true;
    adminJobfairService.retrieveOnlineJobFair(ojfId.value)
        .then( async (res) => {
            loadingStore.loading = false;
            if (res.data.code.toLowerCase() === "success") {
                ojfDetail.value = res.data.result;
            }
        }).catch((res) => {
        loadingStore.loading = false;
        $toast("error", res);
    });
};

// 3. 참가신청 정보 조회
const retrieveRecruitmentHall = async () => {
    loadingStore.loading = true;
    loaded.value = false; // 데이터 받기 전에 화면 안 그리기
    adminCorporationService.retrieveRecruitmentHall(ojfId.value, corpId.value)
        .then( async (res) => {
            loadingStore.loading = false;
            if (res.data.code.toLowerCase() === "success") {
                recruitmentHallInfo.value = res.data.result;
            }
            loaded.value = true; // 데이터 받고 나서 화면 그리기
        }).catch((res) => {
            loadingStore.loading = false;
            $toast("error", res);
    });
};

// 신청 취소
const cancelEntry = (recruitmentHallSeq, joinStateGb) => {
    let paramObj = {
        recruitmentHallSeq: recruitmentHallSeq,
        joinStateGb: joinStateGb,
    }
    adminCorporationService.updateJoinStateBySeq(paramObj)
        .then( async (res) => {
            loadingStore.loading = false;
            if(res.data.code.toLowerCase() === 'success') {
                // const result = res.data.result;
                modalContent.value = ["참가 신청이 취소 되었습니다."];
                await modal.value.show();
                router.push({ name: 'CorporationDetail', params:{id: corpId.value} })
            }
        }).catch( (error) =>  {
        loadingStore.loading = false;
        console.log(error);
    });
}

// 면접 가능일 클릭
const onClickIntvDay = (clickedDay) => {
    inputError.value.intvAbleDay = '';
    isEtcActive.value = false
    recruitmentHallInfo.value.intvInYn = 'Y';

    if (isAllActive.value) {
        // 전체 active 상태였다면 → 전체 해제하고 현재 날짜만 on
        isAllActive.value = false
        isEtcActive.value = false
        recruitmentHallInfo.value.intvInYn = 'Y';

        intvDays.value.forEach(day => {
            day.on = (day === clickedDay)
        })
    } else {
        // 전체 상태가 아닐 때는 토글만
        isEtcActive.value = false
        clickedDay.on = !clickedDay.on
        // 날짜 전체가 선택되었는지 확인
        const allSelected = intvDays.value.every(day => day.on)
        isAllActive.value = allSelected
    }
}

// 면접 가능일 전체
const onClickAll = (force) => {
    inputError.value.intvAbleDay = ''

    if (isAllActive.value && !force) {
        isAllActive.value = false
        isEtcActive.value = false
        recruitmentHallInfo.value.intvInYn = 'Y';

        intvDays.value.forEach(day => {
            day.on = false
        })
    } else {
        // 전체 상태가 아닐 때는 토글만
        isAllActive.value = true
        isEtcActive.value = false
        recruitmentHallInfo.value.intvInYn = 'Y';

        intvDays.value.forEach(day => {
            day.on = true
        })
    }
}

// 면접 가능일 외
const onClickEtc = (force) => {
  inputError.value.intvAbleDay = ''

  if (isEtcActive.value && !force) {
    isEtcActive.value = false
    recruitmentHallInfo.value.intvInYn = 'Y'
  } else {
    isEtcActive.value = true
    isAllActive.value = false
    recruitmentHallInfo.value.intvInYn = 'N'

    intvDays.value.forEach(day => {
      day.on = false
    })
  }
}

const optionData = (key) => {
    if (key === 'intvGb') {
        inputError.value.intvGb = '' ? '면접 방식을 선택하세요.' : '';
    }
}

// 이력서 양식 초기화
const resetFile = (val) => {
    inputError.value.fileYn = '';
    if (val === 'N') {
        recruitmentHallInfo.value.corpResumeAttaches[0].fileName = ''
        recruitmentHallInfo.value.corpResumeAttaches[0].fileDir = ''
        recruitmentHallInfo.value.corpResumeAttaches[0].fileSize = ''
    }
}

// 회사 이력서 양식 업로드
const uploadFileCorpResume = () => {
    const input = document.createElement('input');
    input.type= 'file';
    input.click();

    input.addEventListener('change', function(event){
        // console.log(event.target.files);
        const file = event.target.files[0];

        let paramObj = {
            container: 'private',
            folder: 'corpResumeAttaches',
            file,
        }
        fileService.uploadFile(paramObj)
            .then((res) => {
                // console.log(res);
                if(res.data.code.toLowerCase() === 'success') {
                    const result = res.data.result;
                    // console.log(result);
                    recruitmentHallInfo.value.corpResumeAttaches[0].fileName = result.originalFilename;
                    recruitmentHallInfo.value.corpResumeAttaches[0].fileSize = result.fileSize;
                    recruitmentHallInfo.value.corpResumeAttaches[0].fileDir = result.blobUrl;
                }
            }).catch( (error) =>  {
            console.log(error);
        });
    });
}

// 회사 이력서 양식 삭제
const removeFileCorpResume = () => {
    recruitmentHallInfo.value.corpResumeAttaches[0].fileName = '';
    recruitmentHallInfo.value.corpResumeAttaches[0].fileSize = '';
    recruitmentHallInfo.value.corpResumeAttaches[0].fileDir = '';
    inputError.value.corpResumeAttaches = '';
}

const validateText = (key) => {
    if (key === 'corpName') {
        inputError.value.corpName = '' ? '기업명을 입력하세요.' : '';
    } else if (key === 'ceoName') {
        inputError.value.ceoName = '' ? '대표자명을 입력하세요.' : '';
    } else if (key === 'bizType') {
        inputError.value.bizType = '' ? '업종을 입력하세요.' : '';
    } else if (key === 'foundYm') {
        inputError.value.foundYm = '' ? '창립연월을 입력하세요.' : '';
    } else if (key === 'recruitCnt') {
        inputError.value.recruitCnt = '' ? '모집인원을 입력하세요.' : '';
    } else if (key === 'chargeName') {
        inputError.value.chargeName = '' ? '성명을 입력하세요.' : '';
    } else if (key === 'chargeDepart') {
        inputError.value.chargeDepart = '' ? '부서를 입력하세요.' : '';
    } else if (key === 'chargeJob') {
        inputError.value.chargeJob = '' ? '직위를 입력하세요.' : '';
    } else if (key === 'chargeCompanyNum') {
        inputError.value.chargeCompanyNum = '' ? '회사 직통번호를 입력하세요.' : '';
    } else if (key === 'chargePhoneNum') {
        inputError.value.chargePhoneNum = '' ? '휴대폰 번호를 입력하세요.' : '';
    } else if (key === 'chargeEmail') {
        inputError.value.chargeEmail = '' ? '이메일을 입력하세요.' : '';
    }
}

// 취소
const clickCancel = () => {
    setViewMode(BASIC)

    modalContent.value = ["수정이 취소되었습니다."];
    modal.value.show();
}

// 수정 완료
const updateRecruitmentHall = () => {
    let isInvalid = false;
    let recruitmentHallSeq = recruitmentHallInfo.value.recruitmentHallSeq;

    // 참가 신청 정보
    // 참가 목적
    const { joinPurpose } = recruitmentHallInfo.value;
    inputError.value.joinPurpose = '';

    // ella : 참가 목적 숨김 요청 (20250904)
    // if(!joinPurpose) {
    //     inputError.value.joinPurpose = '참가 목적을 입력하세요.';
    //     isInvalid = true;
    // }

    // 면접 가능일
    const intvAbleDay = _.map(_.filter(intvDays.value, {'on': true}), 'label').join(',');

    const { intvInYn } = recruitmentHallInfo.value;
    inputError.value.intvAbleDay = '';

    if(!intvInYn) {
        inputError.value.intvAbleDay = '면접 가능일을 선택하세요.';
        isInvalid = true;
    } else if (intvInYn === 'Y' && !intvAbleDay) {
        inputError.value.intvAbleDay = '면접 가능일을 선택하세요.';
        isInvalid = true;
    }

    // 면접 방식
    const { intvGb } = recruitmentHallInfo.value;
    inputError.value.intvGb = '';

    if(!intvGb) {
        inputError.value.intvGb = '면접 방식을 선택하세요.';
        isInvalid = true;
    }

    // 이력서 양식, 회사 이력서 양식 첨부, 모집인원
    const { fileYn, recruitCnt } = recruitmentHallInfo.value;
    const corpResumeAttaches =  _.filter(recruitmentHallInfo.value.corpResumeAttaches, attach => {
        return attach.fileName !== null && attach.fileName !== '';
    })
    inputError.value.fileYn = '';
    inputError.value.corpResumeAttaches = '';
    inputError.value.recruitCnt = '';

    if(!fileYn) {
        inputError.value.fileYn = '면접 방식을 선택하세요.';
        isInvalid = true;
    } else if(fileYn === 'Y' && !corpResumeAttaches[0]) {
        inputError.value.corpResumeAttaches = '회사 이력서 양식을 첨부해주세요.';
        isInvalid = true;
    }

    // 모집인원
    if(!recruitCnt || recruitCnt === '0') {
        inputError.value.recruitCnt = '모집인원을 입력하세요.';
        isInvalid = true;
    }

    // 약관 동의
    const agreePrivacyYn = recruitmentHallInfo.value.agreePrivacyYn ? 'Y' : 'N';
    inputError.value.agreePrivacyYn  = false;

    if(agreePrivacyYn === 'N') {
        modalContent.value = ["필수 약관을 확인하고 동의해 주세요."];
        modal.value.show();
        isInvalid = true;
    }

    // 참가 신청 담당자 정보
    const { chargeName, chargeDepart, chargeJob, chargeCompanyNum, chargePhoneNum, chargeEmail } = recruitmentHallInfo.value;
    inputError.value.chargeName  = '';
    inputError.value.chargeDepart  = '';
    inputError.value.chargeJob  = '';
    inputError.value.chargeCompanyNum  = '';
    inputError.value.chargePhoneNum  = '';
    inputError.value.chargeEmail  = '';

    // 성명
    if(!chargeName) {
        inputError.value.chargeName = '성명을 입력하세요.';
        isInvalid = true;
    }

    // 부서
    if(!chargeDepart) {
        inputError.value.chargeDepart = '부서를 입력하세요.';
        isInvalid = true;
    }

    // 직위
    if(!chargeJob) {
        inputError.value.chargeJob = '직위를 입력하세요.';
        isInvalid = true;
    }

    // 회사 직통번호
    if(!chargeCompanyNum) {
        inputError.value.chargeCompanyNum = '회사 직통번호를 입력하세요.';
        isInvalid = true;
    }
    // 20250924(ella): 회사 직통번호 밸리데이션 제거
    // } else if(!$regex.generalPhoneNum.test(chargeCompanyNum)) {
    //     inputError.value.chargeCompanyNum = '회사 직통번호를 숫자로만 입력하세요.';
    //     isInvalid = true;
    // }

    // 휴대전화
    if(!chargePhoneNum || chargePhoneNum.length !== 13) {
        inputError.value.chargePhoneNum = '휴대전화를 입력하세요.';
        isInvalid = true;
    } else if(!$regex.phoneNum.test(chargePhoneNum)) {
        inputError.value.chargePhoneNum = '휴대전화를 숫자로만 입력하세요.';
        isInvalid = true;
    }

    // 이메일
    if(!chargeEmail) {
        inputError.value.chargeEmail = '이메일을 입력하세요.';
        isInvalid = true;
    }

    // 저장 처리
    if(!isInvalid) {
        loadingStore.loading = true;
        jobFairCorpService.ojfCropUpdateRecruitmentHall(
            recruitmentHallSeq,
            joinPurpose, intvInYn, intvAbleDay, intvGb,
            fileYn, corpResumeAttaches, recruitCnt,
            chargeName, chargeDepart, chargeJob, chargeCompanyNum,
            chargePhoneNum, chargeEmail, agreePrivacyYn,
        ).then( async (res) => {
            loadingStore.loading = false;

            if (res.data.code.toLowerCase() === 'success') {
                const result = res.data.result;

                modalContent.value = ["수정이 완료되었습니다."];
                await modal.value.show();

                setViewMode(BASIC);
            }
        }).catch((error) => {
            loadingStore.loading = false;
            console.log(error);
        });
    }
}

// END: 참가신청 정보 관리 TAB

// START: 채용공고 관리 및 지원 현황 TAB
// 1. 채용공고 관리 및 지원 현황 리스트 조회
const retrieveAdminJobNoticeListByCorpIdPaging = () => {
    let paramObj = {
        ojfId: ojfId.value,
        corpId: corpId.value,
        pageNumber: currentPage.value,
        size: pageSize.value,
    }
    loadingStore.loading = true;
    adminCorporationService.retrieveAdminJobNoticeListByCorpIdPaging(paramObj)
        .then( async (res) => {
            loadingStore.loading = false;
            if (res.data.code.toLowerCase() === "success") {
                const result = res.data.result;
                totalPages.value = result.totalPages;
                totalCount.value = result.totalElements;
                jobNoticeList.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
            }
        }).catch((res) => {
        loadingStore.loading = false;
        $toast("error", res);
    });
};

// 2. 채용공고 관리 및 지원 현황 리스트 - 입사지원 리스트
const retrieveOjfResumeListByByJobNoticeSeqAdminPaging = (jobNoticeSeq) => {
    let paramObj = {
        jobNoticeSeq: jobNoticeSeq,
        pageNumber: currentPage.value,
        size: pageSize.value,
    }

    loadingStore.loading = true;
    adminApprovedJoinStateService.retrieveOjfResumeListByByJobNoticeSeqAdminPaging(paramObj)
        .then( (res) => {
            loadingStore.loading = false;
            if(res.data.code.toLowerCase() === 'success') {
                const result = res.data.result;
                // console.log(result);
                totalPages.value = result.totalPages;
                totalCount.value = result.totalElements;
                applicantList.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
            }
        }).catch( (error) =>  {
        console.log(error);
        loadingStore.loading = false;
    });
}

// 채용공고 보기
const onClickJobNotice = async (jobNoticeSeq, corpId) => {
    jobNoticeModal.value.init(false, false, false, ojfId.value);
    jobNoticeModal.value.ojfCropJobNoticeDetail(jobNoticeSeq);
    jobNoticeModal.value.ojfCorpRecruitmentHall(ojfId.value, corpId);
    jobNoticeModal.value.ojfCorporationDetail(corpId);
    await jobNoticeModal.value.show();
}

// 채용공고 수정
const onClickJobNoticeUpdate = (jobNoticeSeq) => {
    router.push({ name: 'JobNoticeUpdate', query : { jobNoticeSeq: jobNoticeSeq, corpId: corpId.value, mode: 'corporation' }});
}

// 채용공고 삭제
const onClickDelete = async (jobNoticeSeq) => {

    modalContent.value = ["채용공고를 삭제하시겠습니까?"];
    okButtonText.value = "삭제";

    const ok = await confirmModal.value.show();
    if(!ok) {
        return -1;
    } else {
        jobFairCorpService.ojfCropJobNoticeDelete ( jobNoticeSeq )
            .then((res) => {
                loadingStore.loading = true;
                if(res.data.code.toLowerCase() === 'success') {
                    modalContent.value = ["채용공고가 삭제되었습니다."];
                    modal.value.show();
                    retrieveAdminJobNoticeListByCorpIdPaging();
                }
            }).catch( (res) =>  {
            loadingStore.loading = false;
            // console.log(res);
        });
    }
}

// 입사지원 상태변경 joinStateGb
const onClickApplyStateGb = async (resumeId, jobNoticeSeq, stateGb) => {

    if (stateGb === "02") {
        modalContent.value = ["지원취소 처리하시겠습니까??", "'지원취소'으로 상태가 변경됩니다."];
        okButtonText.value = "지원취소";
    } else if (stateGb === "03") {
        modalContent.value = ["서류합격 처리하시겠습니까??", "'서류합격'으로 상태가 변경됩니다."];
        okButtonText.value = "서류합격";
    } else if (stateGb === "04") {
        modalContent.value = ["서류불합격 처리하시겠습니까??", "'서류불합격'으로 상태가 변경됩니다."];
        okButtonText.value = "서류불합격";
    }

    const ok = await confirmModal.value.show();
    if(!ok) {
        return -1;
    } else {
        let paramObj = {
            resumeId: resumeId,
            ojfId: ojfId.value,
            jobNoticeSeq: jobNoticeSeq,
            applyStateGb: stateGb,
        }
        jobFairUserService.ojfUpdateApplyStateGb(paramObj)
            .then( (res) => {
                loadingStore.loading = false;
                if(res.data.code.toLowerCase() === 'success') {
                    //const result = res.data.result;
                    modalContent.value = ["변경 하였습니다."];
                    modal.value.show();
                    //$toast('', "삭제를 하였습니다.")

                    setViewMode('RETRIEVE_APPLICANT', jobNoticeSeq);
                } else {
                    modalContent.value = ["변경을 실패하였습니다."];
                    modal.value.show();
                    console.log(res);
                }
            }).catch( (res) =>  {
            loadingStore.loading = false;
            modalContent.value = ["변경을 실패하였습니다."];
            modal.value.show();
            console.log(res);
        });
    }
}
// END: 채용공고 관리 및 지원 현황 TAB

// START: 관심인재 TAB
// 1. 관심인재 리스트 조회
const retrieveCorpInterestHallEntryPaging = () => {
    let paramObj = {
        ojfId: ojfId.value,
        corpId: corpId.value,
        pageNumber: currentPage.value,
        size: pageSize.value,
    }
    loadingStore.loading = true;
    adminCorporationService.retrieveCorpInterestHallEntryPaging(paramObj)
        .then( async (res) => {
            loadingStore.loading = false;
            if (res.data.code.toLowerCase() === "success") {
                const result = res.data.result;
                totalPages.value = result.totalPages;
                totalCount.value = result.totalElements;
                interestEntryList.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
            }
        }).catch((res) => {
        loadingStore.loading = false;
        $toast("error", res);
    });
};

// 이력서 보기
const onClickMyResume = (resumeId) => {
    jobFairResumeModal.value.init(false);
    jobFairResumeModal.value.retrieveJobFairRecordDetail(resumeId, ojfId.value);
    jobFairResumeModal.value.show();
}

// END: 관심인재 TAB

// START: 면접제안 인재 TAB
// 1. 면접제안 인재 리스트 조회
const retrieveAllInterviewProposalEntryListByCorpIdPaging = () => {
    let paramObj = {
        ojfId: ojfId.value,
        corpId: corpId.value,
        pageNumber: currentPage.value,
        size: pageSize.value,
    }
    loadingStore.loading = true;
    adminCorporationService.retrieveAllInterviewProposalEntryListByCorpIdPaging(paramObj)
        .then( async (res) => {
            loadingStore.loading = false;
            if (res.data.code.toLowerCase() === "success") {
                const result = res.data.result;
                totalPages.value = result.totalPages;
                totalCount.value = result.totalElements;
                interviewProposalEntryList.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
            }
        }).catch((res) => {
        loadingStore.loading = false;
        $toast("error", res);
    });
};

// 2. 면접제안 상태 변경
const uppdateIntvPropsState = async (jobNoticeSeq, resumeId, intvPropStateGb, userId) => {
    modalContent.value = ["제안취소 처리하시겠습니까??", "'제안취소'으로 상태가 변경됩니다."];
    okButtonText.value = "제안취소";
    const ok = await confirmModal.value.show();
    if(!ok) {
        return false;
    }

    let paramObj = {
        jobNoticeSeq: jobNoticeSeq,
        resumeId: resumeId,
        intvPropStateGb: intvPropStateGb,
        userId: userId,
    }
    loadingStore.loading = true;
    jobFairUserService.ojfUpdateIntvPropsState(paramObj)
        .then( async (res) => {
            loadingStore.loading = false;
            if (res.data.code.toLowerCase() === "success") {
                modalContent.value = ["변경하였습니다."];
                modal.value.show();
                retrieveAllInterviewProposalEntryListByCorpIdPaging();
            }
        }).catch((res) => {
        loadingStore.loading = false;
        $toast("error", res);
    });
};

// END: 면접제안 인재 TAB

// tabIndex 변경
const onChangeTab = async ({ value }) => {
    tabIndex.value = value;

    currentPage.value = 1;
    pageSize.value = 10;
    searchOpen.value = true;

    await ojfIntvDay();
    retrieveList();
}

// 페이지 선택
const onSelectPage = (pageNo) => {
    currentPage.value = pageNo;
    retrieveList();
};

// 페이지 사이즈 변경. 10,30,50,100
const onSelectPageSize = (option) => {
    pageSize.value = option.value;
    currentPage.value = 1;
    retrieveList();
}

// tab별 리스트 불러 오기 공통 함수
const retrieveList = () => {
    if(tabIndex.value === 1) { // 참가신청 정보 관리
        ojfIntvDay(); // 면접 가능일 조회
        retrieveRecruitmentHall();
    } else if(tabIndex.value === 2) { // 채용공고 관리 및 지원 현황
        retrieveAdminJobNoticeListByCorpIdPaging();
    } else if(tabIndex.value === 3) { // 관심인재
        retrieveCorpInterestHallEntryPaging();
    } else if(tabIndex.value === 4) { // 면접제안 인재
        retrieveAllInterviewProposalEntryListByCorpIdPaging();
    }
    viewMode.value = BASIC;
}

// 행사 상세로 이동
const goOjfDetail = () => {
    router.push({ name: 'OjfDetail', params:{id: ojfId.value} })
}

// 행사 상세 새 창으로 열기
const openOjfDetailInNewWindow = () => {
    const url = router.resolve({
        name: 'OjfDetail',
        params: { id: ojfId.value },
    }).href;

    const windowFeatures = 'width=1490,height=825,top=100,left=100,resizable=yes,scrollbars=yes';
    window.open(url, '_blank', windowFeatures);
}

// 뒤로
const goBack = () => {
    router.push({ name: 'CorporationDetail', params:{id: corpId.value} })
}
</script>

<template>
    <div class="container">
        <div class="flex-wrap admin">
            <SNBAdmin class="snb" />
            <div class="content">
                <div class="sub-title">
                    <h3>{{ ojfDetail.ojfName }}</h3>
                </div>
                <div class="list-top space-between">
                    <KSASecondaryButton class="gray_button" text="목록" @click="goBack" />
                    <div>
                        <KSASecondaryButton class="gray_button ml-xs" text="행사 상세 새 창 보기" @click="openOjfDetailInNewWindow" />
                    </div>
                </div>
                <KSATab style="grid-template-columns: repeat(4, 1fr);" class="mb-sm"
                    :options="tabOptions"
                    @change="onChangeTab"
                />

                <!-- s: tabIndex 1 : 참가신청 정보 관리 ------------------------------------------------------------->
                <div class="table-form" v-if="loaded && tabIndex === 1">
                    <div class="state-box">
                        ● 현재 참가 신청 상태 : <span>{{ $gbNameKR('USER_JOIN_STATE_GB', recruitmentHallInfo.joinStateGb) }}</span>
                    </div>
                    <!-- s: tabIndex 1 : 참가신청 정보 관리 : 보기 모드------------------------------------------------->
                    <div v-if="viewMode === BASIC">
                        <table class="vertical align-left">
                            <colgroup>
                                <col width="20%" />
                                <col />
                            </colgroup>
                            <tr>
                                <th colspan="2">● 참가신청 정보</th>
                            </tr>
                            <!-- ella : 참가 목적 숨김 요청 (20250904) -->
<!--                            <tr>-->
<!--                                <th>참가 목적</th>-->
<!--                                <td>{{ recruitmentHallInfo.joinPurpose }}</td>-->
<!--                            </tr>-->
                            <tr>
                                <th>면접 가능일</th>
                                <td class="word-break-all">
                                    <span v-if="recruitmentHallInfo.intvInYn === 'Y' && isAllSelected">
                                        전체 일정 가능
                                        [{{ recruitmentHallInfo.intvAbleDay }}]
                                    </span>
                                    <span v-else-if="recruitmentHallInfo.intvInYn === 'Y'">{{ recruitmentHallInfo.intvAbleDay }}</span>
                                    <span v-else>면접 일정 조율</span>
                                </td>
                            </tr>
                            <tr>
                                <th>면접 방식</th>
                                <td>{{ $gbNameKR('INTV_GB', recruitmentHallInfo.intvGb) }}</td>
                            </tr>
                            <tr>
                                <th>이력서 양식</th>
                                <td>
                                    <p v-if="recruitmentHallInfo.fileYn === 'N'">협회 이력서</p>
                                    <p v-else>회사 이력서</p>
                                </td>
                            </tr>
                            <tr v-if="recruitmentHallInfo.fileYn === 'Y'">
                                <th>회사 이력서 양식 첨부</th>
                                <td>
                                    <p class="file" v-for="corpResumeAttaches in recruitmentHallInfo.corpResumeAttaches" @click="$downloadForPrivate({fileName:corpResumeAttaches.fileName, fileDir: corpResumeAttaches.fileDir})">
                                        {{ corpResumeAttaches.fileName }}
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <th>모집인원</th>
                                <td>{{ recruitmentHallInfo.recruitCnt }}명</td>
                            </tr>
                        </table>
                        <table class="vertical align-left mt-sm">
                            <colgroup>
                                <col width="20%" />
                                <col />
                            </colgroup>
                            <tr>
                                <th colspan="2">● 참가 신청 담당자 정보</th>
                            </tr>
                            <tr>
                                <th>성명</th>
                                <td>{{ recruitmentHallInfo.chargeName }}</td>
                            </tr>
                            <tr>
                                <th>부서</th>
                                <td>{{ recruitmentHallInfo.chargeDepart }}</td>
                            </tr>
                            <tr>
                                <th>직위</th>
                                <td>{{ recruitmentHallInfo.chargeJob }}</td>
                            </tr>
                            <tr>
                                <th>회사 직통번호</th>
                                <td>{{ $generalPhoneNum(recruitmentHallInfo.chargeCompanyNum) }}</td>
                            </tr>
                            <tr>
                                <th>휴대전화</th>
                                <td>{{ $phoneNum(recruitmentHallInfo.chargePhoneNum) }}</td>
                            </tr>
                            <tr>
                                <th>이메일</th>
                                <td>{{ recruitmentHallInfo.chargeEmail }}</td>
                            </tr>
                        </table>
                        <div v-if="recruitmentHallInfo.joinStateGb === '01' || recruitmentHallInfo.joinStateGb === '02'" class="btn-wrap space-between mb-xs">
                            <KSASecondaryButton class="red_button" text="신청 취소" @click="cancelEntry(recruitmentHallInfo.recruitmentHallSeq, '04')" />
                            <KSAPrimaryButton class="gray_button" text="수정" @click="setViewMode(ENTRY_UPDATE)" />
                        </div>
                    </div>
                    <!-- e: tabIndex 1 : 참가신청 정보 관리 : 보기 모드------------------------------------------------->
                    <!-- s: tabIndex 1 : 참가신청 정보 관리 : 수정 모드------------------------------------------------->
                    <div v-else-if="viewMode === ENTRY_UPDATE">
                        <table class="vertical align-left">
                            <colgroup>
                                <col width="20%" />
                                <col />
                            </colgroup>
                            <tr>
                                <th colspan="2">● 참가 신청 정보</th>
                            </tr>
                            <!-- ella : 참가 목적 숨김 요청 (20250904) -->
<!--                            <tr>-->
<!--                                <th class="mark">참가 목적</th>-->
<!--                                <td>-->
<!--                                    <KSAInput v-model="recruitmentHallInfo.joinPurpose" placeholder="ex) 취업 기회 탐색, 채용 정보 획득, 기업의 인사 담당자 또는 채용 관계자와의 네트워킹 등" />-->
<!--                                    <p>-->
<!--                                        <small style="color:#E71414;">{{ inputError.joinPurpose }}</small>-->
<!--                                    </p>-->
<!--                                </td>-->
<!--                            </tr>-->
                            <tr>
                                <th rowspan="2">
                                    <span class="mark">면접 가능일</span>
                                </th>
                                <td>
                                    <ul class="grid category-wrap">
                                        <li :class="{'active': isAllActive}" @click="onClickAll(false)">전체 일정 가능</li>
                                        <li :class="{'active': day.on, 'disabled': isAllActive}" v-for="day in intvDays" @click="onClickIntvDay(day)">
                                            {{ day.label }}
                                        </li>
                                    </ul>
                                </td>
                            </tr>
                            <tr>
                                <td class="word-break-all">
                                    <ul class="grid category-wrap">
                                        <li :class="{'active': isEtcActive}" @click="onClickEtc(false)">면접 일정 조율</li>
                                    </ul>
                                    <p>
                                        <small style="color:#E71414;">{{ inputError.intvAbleDay }}</small>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <th class="mark">면접 방식</th>
                                <td>
                                    <template v-for="option in $gb.INTV_GB">
                                        <KSARadioButton class="check-box mr-lg" name="intvGb" :label="option.label" v-model="recruitmentHallInfo.intvGb" :value="option.value" @change="optionData('intvGb')"/>
                                    </template>
                                    <p>
                                        <small style="color:#E71414;">{{ inputError.intvGb }}</small>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <th class="mark">이력서 양식</th>
                                <td>
                                    <p class="red mb-xs" v-if="recruitmentHallInfo.fileYn === 'Y'">※ ‘협회 이력서’ 양식 사용을 권장합니다.</p>
                                    <KSARadioButton class="check-box mr-lg" label="협회 이력서" v-model="recruitmentHallInfo.fileYn" value="N" @change="resetFile('N')" />
                                    <KSARadioButton class="check-box mr-lg" label="회사 이력서" v-model="recruitmentHallInfo.fileYn" value="Y" @change="resetFile('Y')" />
                                    <p>
                                        <small style="color:#E71414;">{{ inputError.fileYn }}</small>
                                    </p>
                                </td>
                            </tr>
                            <tr v-if="recruitmentHallInfo.fileYn === 'Y'">
                                <th class="mark">회사 이력서 양식 첨부</th>
                                <td class="text-box">
                                    <div class="mb-xs">
                                        <p class="red">* 첨부 전 반드시 확인해주세요!</p>
                                        - 의도치 않게 개인정보가 노출될 수 있으므로 첨부하는 파일에 <span class="red">실제 개인정보가 입력되어 있는지 확인</span>해주세요.<br>
                                        - 첨부된 파일로 인해 개인정보가 노출될 경우 책임은 첨부한 기업에 있음을 알려드립니다.<br>
                                        - 파일명은 <span class="red">‘기업명_이력서양식’</span> 으로 첨부해 주세요.
                                    </div>
                                    <div class="grid file" v-for="(attach, index) in recruitmentHallInfo.corpResumeAttaches" :key="index">
                                        <KSAPrimaryButton text="파일 선택" @click="uploadFileCorpResume(attach)" />
                                        <KSAInput class="input icon" v-model="attach.fileName" placeholder="파일 선택" disabled />
                                        <button class="delete" @click="removeFileCorpResume(recruitmentHallInfo, index)"><i></i></button>
                                    </div>
                                    <small>최대 200MB 까지 첨부 가능</small>
                                    <p>
                                        <small style="color:#E71414;">{{ inputError.corpResumeAttaches }}</small>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <th class="mark">모집인원</th>
                                <td>
                                    <div class="grid half">
                                        <KSAInputNumber v-model="recruitmentHallInfo.recruitCnt" @input="validateText('recruitCnt')" placeholder="0" maxLength="3" /> 명
                                    </div>
                                    <p>
                                        <small style="color:#E71414;">{{ inputError.recruitCnt }}</small>
                                    </p>
                                </td>
                            </tr>
                        </table>
                        <table class="vertical align-left mt-sm">
                            <colgroup>
                                <col width="20%" />
                                <col />
                            </colgroup>
                            <tr>
                                <th colspan="2">● 참가 신청 담당자 정보</th>
                            </tr>
                            <tr>
                                <th class="mark">성명</th>
                                <td>
                                    <KSAInput class="text-box" v-model="recruitmentHallInfo.chargeName" @input="validateText('chargeName')" placeholder="성명을 입력하세요." />
                                    <p>
                                        <small style="color:#E71414;">{{ inputError.chargeName }}</small>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <th class="mark">부서</th>
                                <td>
                                    <KSAInput class="text-box" v-model="recruitmentHallInfo.chargeDepart" @input="validateText('chargeDepart')" placeholder="부서를 입력하세요." />
                                    <p>
                                        <small style="color:#E71414;">{{ inputError.chargeDepart }}</small>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <th class="mark">직위</th>
                                <td>
                                    <KSAInput class="text-box" v-model="recruitmentHallInfo.chargeJob" @input="validateText('chargeJob')" placeholder="직위를 입력하세요." />
                                    <p>
                                        <small style="color:#E71414;">{{ inputError.chargeJob }}</small>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <th class="mark">회사 직통번호</th>
                                <td>
                                  <!-- 20250924(ella): 회사 직통번호 밸리데이션 제거 -->
<!--                                    <KSAInputGeneralPhoneNum class="text-box" label="" v-model="recruitmentHallInfo.chargeCompanyNum" @input="validateText('chargeCompanyNum')" placeholder="회사 직통번호를 입력하세요." />-->
                                  <KSAInput class="text-box" v-model="recruitmentHallInfo.chargeCompanyNum" @input="validateText('chargeCompanyNum')" placeholder="회사 직통번호를 입력하세요. ex) 02-1234-5678" />
                                    <p>
                                        <small style="color:#E71414;">{{ inputError.chargeCompanyNum }}</small>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <th class="mark">휴대전화</th>
                                <td>
                                    <KSAInputPhoneNum class="input" v-model="recruitmentHallInfo.chargePhoneNum" label="" @input="validateText('chargePhoneNum')" placeholder="휴대폰 번호를 입력하세요."/>
                                    <p>
                                        <small style="color:#E71414;">{{ inputError.chargePhoneNum }}</small>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <th class="mark">이메일</th>
                                <td>
                                    <KSAInput class="text-box" v-model="recruitmentHallInfo.chargeEmail" @input="validateText('chargeEmail')" placeholder="이메일 주소를 입력하세요." />
                                    <p>
                                        <small style="color:#E71414;">{{ inputError.chargeEmail }}</small>
                                    </p>
                                </td>
                            </tr>
                        </table>
                        <div class="btn-wrap space-between">
                            <KSASecondaryButton class="gray_button" text="취소" @click="clickCancel" />
                            <KSASecondaryButton text="수정 완료" @click="updateRecruitmentHall" />
                        </div>
                    </div>
                    <!-- e: tabIndex 1 : 참가신청 정보 관리 : 수정 모드------------------------------------------------->
                </div>
                <!-- e: tabIndex 1 : 참가신청 정보 관리 ------------------------------------------------------------->

                <!-- s: tabIndex 2 : 채용공고 관리 및 지원 현황 ------------------------------------------------------>
                <div v-else-if="tabIndex === 2">
                    <div v-show="viewMode === BASIC">
                        <!-- s: 버튼영역 -->
                        <div class="list-top">
                            <p>전체 <span>{{ $number(totalCount)}}</span>개</p>
                            <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize"/>
                        </div>
                        <!-- e: 버튼영역 -->
                        <div class="table-form">
                            <table class="horizontal" v-if="jobNoticeList !== null">
                                <tr>
                                    <th>NO</th>
                                    <th>채용공고 제목</th>
                                    <th>입사지원</th>
                                    <th>최근 수정일</th>
                                    <th>등록일</th>
                                    <th>등록 마스터</th>
                                    <th>관리</th>
                                </tr>
                                <tr v-for="ojf in jobNoticeList">
                                    <td>{{ ojf.boardNo }}</td>
                                    <td class="clickable blue text-overflow long-overflow" @click="onClickJobNotice(ojf.jobNoticeSeq, ojf.corpId)">{{ ojf.jobName }}</td>
                                    <td class="clickable blue" @click="setViewMode(RETRIEVE_APPLICANT, ojf.jobNoticeSeq)">{{ ojf.applicantCnt }}</td>
                                    <td>{{ $yyyymmddHHmmss(ojf.modifyDate) }}</td>
                                    <td>{{ $yyyymmddHHmmss(ojf.createDate) }}</td>
                                    <td class="clickable blue">
                                        <RouterLink :to="{name:'MemberDetail', params:{id: ojf.createId}}">{{ ojf.userName }}</RouterLink>
                                    </td>
                                    <td>
                                        <!-- 승인/반려 처리 이전 = 수정가능, 삭제 불가 -->
                                        <!-- 승인처리 이후 + 입사지원 없는 경우 = 수정가능, 삭제 가능 -->
                                        <div v-if="ojf.modifyYn === 'Y'">
                                            <KSATextButton class="clickable" text="수정" @click="onClickJobNoticeUpdate(ojf.jobNoticeSeq)"/>
                                        </div>
                                        <div v-if="ojf.deleteYn === 'Y'">
                                            <KSATextButton class="clickable red_button" text="삭제" @click="onClickDelete(ojf.jobNoticeSeq)"/>
                                        </div>
                                        <div v-if="ojf.modifyYn === 'N' && ojf.deleteYn === 'N'"> - </div>
                                    </td>
                                </tr>
                            </table>
                            <div class="result-none" v-if="jobNoticeList.length === 0">
                                등록된 채용공고가 없습니다.
                            </div>
                        </div>
                        <Paginate
                            v-if="jobNoticeList.length > 0"
                            v-model="currentPage"
                            :page-count="totalPages"
                            :click-handler="onSelectPage"
                        />
                    </div>
                    <!-- s: tabIndex2 2 : 채용공고 관리 및 지원 현황 - 입사지원 상세 ------------------------------------------------->
                    <div v-show="viewMode !== BASIC">
                        <div v-if="viewMode === RETRIEVE_APPLICANT">
                            <KSASecondaryButton text="이전" @click="setViewMode(BASIC)" />
                            <div class="list-top">
                                <p>총 <span>{{ $number(totalCount)}}</span>명</p>
                                <div>
                                    <KSAPrimaryButton text="채용공고 보기" @click="onClickJobNotice(applicantJobNoticeSeq, corpId)" />
                                </div>
                            </div>
                            <div class="table-form">
                                <table class="horizontal" v-if="applicantList.length !== 0">
                                    <tr>
                                        <th>NO</th>
                                        <th>회원코드</th>
                                        <th>이름</th>
                                        <th>아이디</th>
                                        <th>이력서 제목</th>
                                        <th v-show="applicantList[0].fileYn === 'N'">관심 카테고리</th>
                                        <th>면접 가능일</th>
                                        <th>입사 지원일</th>
                                        <th>상태</th>
                                        <th>상태변경</th>
                                    </tr>
                                    <tr v-for="ojf in applicantList">
                                        <td>{{ ojf.boardNo }}</td>
                                        <td>{{ ojf.userCd }}</td>
                                        <td class="clickable blue">
                                            <RouterLink :to="{name: 'MemberDetail', params:{id: ojf.userCd}}">{{ ojf.userName }}</RouterLink>
                                        </td>
                                        <td>{{ ojf.userLoginId }}</td>
                                        <td class="text-overflow long-overflow">
                                        <span class="file" v-if="ojf.fileYn === 'Y'" v-for="resumeAttaches in ojf.resumeAttaches" @click="$downloadForPrivate({fileName:resumeAttaches.fileName, fileDir: resumeAttaches.fileDir })">
                                          {{ resumeAttaches.fileName }}
                                        </span>
                                            <span class="clickable blue" v-else @click="onClickMyResume(ojf.resumeId)">
                                          {{ ojf.title }}
                                        </span>
                                        </td>
                                        <td v-show="applicantList[0].fileYn === 'N'">{{ ojf.interests }}</td>
                                        <td class="word-break-all">
                                            <span v-if="ojf.intvInYn === 'Y' && isAllSelected2">
                                                전체 일정 가능
                                                [{{ ojf.intvAbleDay }}]
                                            </span>
                                            <span v-else-if="ojf.intvInYn === 'Y'">
                                                {{ ojf.intvAbleDay }}
                                            </span>
                                            <span v-else>면접 일정 조율</span>
                                        </td>
                                        <td>{{ $yyyymmddHHmmss(ojf.createDate) }}</td>
                                        <td>
                                            <span v-if="ojf.applyStateGb === '01'">{{ $gbNameKR('APPLY_STATE_GB',ojf.applyStateGb) }}</span>
                                            <span v-else-if="ojf.applyStateGb === '03'" class="blue">{{ $gbNameKR('APPLY_STATE_GB',ojf.applyStateGb) }}</span>
                                            <span v-else-if="ojf.applyStateGb === '04'" class="red">{{ $gbNameKR('APPLY_STATE_GB',ojf.applyStateGb) }}</span>
                                            <span v-else class="gray">{{ $gbNameKR('APPLY_STATE_GB',ojf.applyStateGb) }}</span>
                                        </td>
                                        <td>
                                            <div v-if="ojf.applyStateGb === '01'">
                                                <KSATextButton class="clickable blue_button" text="서류합격" @click="onClickApplyStateGb(ojf.resumeId, ojf.jobNoticeSeq, '03')"/>
                                                &nbsp;
                                                <KSATextButton class="clickable red_button" text="불합격" @click="onClickApplyStateGb(ojf.resumeId, ojf.jobNoticeSeq, '04')"/>
                                            </div>
                                            <div v-else>
                                                -
                                            </div>
                                        </td>
                                    </tr>
                                </table>
                                <table class="horizontal" v-if="applicantList.length === 0">
                                    <tr>
                                        <th>NO</th>
                                        <th>회원코드</th>
                                        <th>이름</th>
                                        <th>아이디</th>
                                        <th>이력서 제목</th>
                                        <th>관심 카테고리</th>
                                        <th>면접 가능일</th>
                                        <th>입사 지원일</th>
                                        <th>상태</th>
                                        <th>상태변경</th>
                                    </tr>
                                </table>
                                <div class="result-none" v-if="applicantList.length === 0">
                                    입사지원 내역이 없습니다.
                                </div>
                            </div>
                            <Paginate
                                v-if="applicantList.length > 0"
                                v-model="currentPage"
                                :page-count="totalPages"
                                :click-handler="onSelectPage"
                            />
                        </div>
                    </div>
                    <!-- e: tabIndex2 2 : 채용공고 관리 및 지원 현황 - 입사지원 상세------------------------------------------------>
                </div>
                <!-- e: tabIndex 2 : 채용공고 관리 및 지원 현황 ------------------------------------------------------>

                <!-- s: tabIndex 3 : 관심인재 --------------------------------------------------------------------->
                <div v-else-if="tabIndex === 3">
                    <!-- s: 버튼영역 -->
                    <div class="list-top">
                        <p>전체 <span>{{ $number(totalCount)}}</span>개</p>
                        <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize"/>
                    </div>
                    <!-- e: 버튼영역 -->
                    <div class="table-form">
                        <table class="horizontal" v-if="interestEntryList !== null">
                            <tr>
                                <th>NO</th>
                                <th>회원코드</th>
                                <th>이름</th>
                                <th>아이디</th>
                                <th>이력서 제목</th>
                                <th>관심 카테고리</th>
                                <th>면접 가능일</th>
                                <th>등록일</th>
                                <th>등록 마스터</th>
                            </tr>
                            <tr v-for="ojf in interestEntryList">
                                <td>{{ ojf.boardNo }}</td>
                                <td>{{ ojf.userCd }}</td>
                                <td class="clickable blue">
                                    <RouterLink :to="{name:'MemberDetail', params:{id: ojf.userCd}}">{{ ojf.userName }}</RouterLink>
                                </td>
                                <td>{{ ojf.userLoginId }}</td>
                                <td class="clickable blue text-overflow long-overflow">
                                    <span class="file" v-if="ojf.fileYn === 'Y'" v-for="resumeAttaches in ojf.resumeAttaches" @click="$downloadForPrivate({fileName:resumeAttaches.fileName, fileDir: resumeAttaches.fileDir })">
                                        {{ resumeAttaches.fileName }}
                                    </span>
                                    <span class="clickable blue" v-else @click="onClickMyResume(ojf.resumeId)">
                                        {{ ojf.title }}
                                    </span>
                                </td>
                                <td>{{ ojf.interests }}</td>
                                <td class="word-break-all">
                                    <span v-if="ojf.intvInYn === 'Y' && isAllSelected3">
                                        전체 일정 가능
                                        [{{ ojf.intvAbleDay }}]
                                    </span>
                                    <span v-else-if="ojf.intvInYn === 'Y'">
                                        {{ ojf.intvAbleDay }}
                                    </span>
                                    <span v-else>면접 일정 조율</span>
                                </td>
                                <td>{{ $yyyymmddHHmmss(ojf.modifyDate) }}</td>
                                <td class="clickable blue">
                                    <RouterLink :to="{name:'MemberDetail', params:{id: ojf.masterUserId}}">{{ ojf.masterUserName }}</RouterLink>
                                </td>
                            </tr>
                        </table>
                        <div class="result-none" v-if="interestEntryList.length === 0">
                            등록된 관심인재가 없습니다.
                        </div>
                    </div>
                    <Paginate
                        v-if="interestEntryList.length > 0"
                        v-model="currentPage"
                        :page-count="totalPages"
                        :click-handler="onSelectPage"
                    />
                </div>
                <!-- e: tabIndex 3 : 관심인재 -------------------------------------------------------------------->

                <!-- s: tabIndex 4 : 면접제안 인재 ---------------------------------------------------------------->
                <div v-else-if="tabIndex === 4">
                    <!-- s: 버튼영역 -->
                    <div class="list-top">
                        <p>전체 <span>{{ $number(totalCount)}}</span>개</p>
                        <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize"/>
                    </div>
                    <!-- e: 버튼영역 -->
                    <div class="table-form">
                        <table class="horizontal" v-if="interviewProposalEntryList !== null">
                            <tr>
                                <th>NO</th>
                                <th>회원코드</th>
                                <th>이름</th>
                                <th>아이디</th>
                                <th>이력서 제목</th>
                                <th>관심 카테고리</th>
                                <th>면접 가능일</th>
                                <th>면접제안 공고 제목</th>
                                <th>제안일</th>
                                <th>제안 마스터</th>
                                <th>상태</th>
                                <th>상태변경</th>
                            </tr>
                            <tr v-for="ojf in interviewProposalEntryList">
                                <td>{{ ojf.boardNo }}</td>
                                <td>{{ ojf.userCd }}</td>
                                <td class="clickable blue">
                                    <RouterLink :to="{name:'MemberDetail', params:{id: ojf.userCd}}">{{ ojf.userName }}</RouterLink>
                                </td>
                                <td>{{ ojf.userLoginId }}</td>
                                <td class="clickable blue text-overflow long-overflow">
                                    <span class="file" v-if="ojf.fileYn === 'Y'" v-for="resumeAttaches in ojf.resumeAttaches" @click="$downloadForPrivate({fileName:resumeAttaches.fileName, fileDir: resumeAttaches.fileDir })">
                                        {{ resumeAttaches.fileName }}
                                    </span>
                                    <span class="clickable blue" v-else @click="onClickMyResume(ojf.resumeId)">
                                        {{ ojf.title }}
                                    </span>
                                </td>
                                <td>{{ ojf.interests }}</td>
                                <td class="word-break-all">
                                    <span v-if="ojf.intvInYn === 'Y' && isAllSelected4">
                                        전체 일정 가능
                                        [{{ ojf.intvAbleDay }}]
                                    </span>
                                    <span v-else-if="ojf.intvInYn === 'Y'">
                                        {{ ojf.intvAbleDay }}
                                    </span>
                                    <span v-else>면접 일정 조율</span>
                                </td>
                                <td>{{ corpId }}/{{ ojf.jobNoticeSeq }}/{{ ojf.jobName }}</td>
                                <td>{{ $yyyymmddHHmmss(ojf.createDate) }}</td>
                                <td class="clickable blue">
                                    <RouterLink :to="{name:'MemberDetail', params:{id: ojf.masterUserId}}">{{ ojf.masterUserName }}</RouterLink>
                                </td>
                                <td :class="ojf.intvPropStateGb === '02' ? 'blue' : ojf.intvPropStateGb === '03' ? 'red' : ojf.intvPropStateGb === '04' ? 'gray' : ''">
                                    {{ $gbNameKR("ADMIN_INTV_PROP_STATE_GB", ojf.intvPropStateGb) }}
                                </td>
                                <td class="clickable red">
                                    <span v-if="ojf.intvPropStateGb === '01'" @click="uppdateIntvPropsState(ojf.jobNoticeSeq,ojf.resumeId,'04',ojf.userCd)" >제안취소</span>
                                </td>
                            </tr>
                        </table>
                        <div class="result-none" v-if="interviewProposalEntryList.length === 0">
                            등록된 관심인재가 없습니다.
                        </div>
                    </div>
                    <Paginate
                        v-if="interviewProposalEntryList.length > 0"
                        v-model="currentPage"
                        :page-count="totalPages"
                        :click-handler="onSelectPage"
                    />
                </div>
                <!-- e: tabIndex 4 : 면접제안 인재 ---------------------------------------------------------------->

            </div>
        </div>
    </div>
    <AlertModal ref="modal" :content="modalContent" />
    <ConfirmModal ref="confirmModal" :content="modalContent" :okButtonText="okButtonText" cancelButtonText="취소" />
    <JobFairJobNoticeModal ref="jobNoticeModal" /> <!-- 채용공고 보기 -->
    <JobFairResumeModal ref="jobFairResumeModal" /> <!-- 면접제안 인재 > 이력서 상세 -->
</template>

<style lang="scss" scoped>
@import "../../../../assets/scss/theme";

.textarea-box {
    height: 100px;
}
.blue {
    color: $blue_color;
}
.red {
    color: $red_color;
}
.clickable {
    text-decoration: underline;
    cursor: pointer;
}
.file {
    color: $blue_color;
    cursor: pointer;
}

.table-form {
    table {
        &.vertical {
            &.align-left {
                th {
                    text-align: left;
                    span {
                        display: block;
                        font-size: $font-size_xs;
                        color: $gray_color;
                        font-weight: normal;
                        &.mark {
                            font-size: $font-size_sm;
                            color: $black_color;
                            font-weight: bold;
                        }
                    }
                }
                tr th small {
                    display: block;
                    font-size: 14px;
                    color: #86889a;
                    font-weight: 400;
                }
            }
        }
    }
    .disabled {
        cursor: default;
        background: $gray-regular_color;
        border-color: $gray-regular_color;
        color: #fff;
    }
}

.state-box {
    border: 2px solid $black_color;
    margin: 10px 0px;
    padding: 10px 20px;
    span {
        color: $blue_color;
    }
}
</style>